import React, { useEffect, useMemo, useState } from "react";
import { Camera, CheckCircle2, ImagePlus, Loader2, UploadCloud } from "lucide-react";
import { firebaseConfig } from "../firebase";
import {
  createThumbnailFile,
  DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
  DEFAULT_TARGET_IMAGE_BYTES,
  optimizeImageFile,
  validateManagedImageFile,
} from "../utils/imageOptimization";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read image."));
    reader.readAsDataURL(file);
  });

const FABRIC_UPLOAD_FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FABRIC_UPLOAD_FUNCTIONS_BASE_URL ||
  `https://asia-south1-${firebaseConfig.projectId}.cloudfunctions.net`;
const MAX_FABRIC_QR_UPLOAD_BYTES = 8 * 1024 * 1024;

const getFabricUploadEndpoint = (functionName = "") =>
  `${FABRIC_UPLOAD_FUNCTIONS_BASE_URL.replace(/\/+$/g, "")}/${functionName}`;

const FABRIC_QR_STEP_INITIAL = {
  session: "idle",
  file: "idle",
  upload: "idle",
  complete: "idle",
};

const isFabricQrDebugVisible = () =>
  process.env.NODE_ENV !== "production" ||
  new URLSearchParams(window.location.search).get("debug") === "1";

const redactFabricUploadTokenFromUrl = (value = "") => {
  try {
    const url = new URL(String(value || ""));
    ["t", "token"].forEach((key) => {
      if (url.searchParams.has(key)) url.searchParams.set(key, "[redacted]");
    });
    return url.toString();
  } catch (error) {
    return String(value || "").replace(/([?&](?:t|token)=)[^&#]+/gi, "$1[redacted]");
  }
};

const parseFabricUploadRoute = () => {
  const params = new URLSearchParams(window.location.search);
  const pathMatch = window.location.pathname.match(/^\/fabric-upload\/([^/?#]+)/);
  const shortPathMatch = window.location.pathname.match(/^\/u\/([^/?#]+)/);
  return {
    sessionId:
      decodeURIComponent(pathMatch?.[1] || "") ||
      params.get("sessionId") ||
      params.get("s") ||
      "",
    shortCode:
      decodeURIComponent(shortPathMatch?.[1] || "") ||
      params.get("shortCode") ||
      params.get("code") ||
      params.get("u") ||
      "",
    token: params.get("token") || params.get("t") || "",
  };
};

const postFabricUploadJson = async (functionName, payload = {}) => {
  const response = await fetch(getFabricUploadEndpoint(functionName), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    const error = new Error(data?.message || "Could not complete this upload request.");
    error.code = data?.code || `http-${response.status}`;
    throw error;
  }
  return data;
};

const getFabricUploadJson = async (functionName, payload = {}) => {
  const url = new URL(getFabricUploadEndpoint(functionName));
  Object.entries(payload).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  const response = await fetch(url.toString(), { method: "GET" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    const error = new Error(data?.message || "Could not complete this upload request.");
    error.code = data?.code || `http-${response.status}`;
    throw error;
  }
  return data;
};

const getFriendlyFabricUploadError = (error, fallback = "Could not upload the photo. Please ask the showroom to try again.") => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  if (code.includes("not-found")) return "This upload link is invalid.";
  if (code.includes("failed-precondition") || message.includes("expired")) {
    return "This upload link has expired. Please ask the showroom to generate a new QR.";
  }
  if (code.includes("invalid-argument") && (message.includes("large") || message.includes("size"))) {
    return "Photo is too large. Please choose a smaller image.";
  }
  if (code.includes("invalid-argument") && (message.includes("jpg") || message.includes("png") || message.includes("webp"))) {
    return "Please upload a JPG, PNG, or WEBP image.";
  }
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Could not upload the photo. Please ask the showroom to try again.";
  }
  return fallback;
};

const dataUrlToPayload = (dataUrl = "", fallbackContentType = "image/jpeg") => {
  const match = String(dataUrl || "").match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    return {
      contentType: fallbackContentType,
      base64: String(dataUrl || ""),
    };
  }
  return {
    contentType: match[1] || fallbackContentType,
    base64: match[2] || "",
  };
};

const formatExpiry = (value = "") => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "short",
  });
};

export default function FabricPhotoUploadPage() {
  const { sessionId, shortCode, token } = useMemo(() => parseFabricUploadRoute(), []);
  const [sessionState, setSessionState] = useState({
    loading: true,
    error: "",
    session: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [mobileSteps, setMobileSteps] = useState(FABRIC_QR_STEP_INITIAL);
  const showMobileDebug = isFabricQrDebugVisible();

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      setMobileSteps({
        ...FABRIC_QR_STEP_INITIAL,
        session: "loading",
      });
      console.log("FABRIC_QR_MOBILE_PAGE_LOAD", {
        sessionId,
        shortCode,
        hasToken: Boolean(token),
        userAgent: navigator.userAgent || "",
        currentUrl: redactFabricUploadTokenFromUrl(window.location.href),
        firebaseProjectId: firebaseConfig.projectId,
        sessionEndpoint: getFabricUploadEndpoint("getFabricPhotoUploadSessionPublicHttp"),
      });

      const hasLegacySessionLink = Boolean(sessionId && token);
      const hasShortUploadLink = Boolean(shortCode);
      if (!hasLegacySessionLink && !hasShortUploadLink) {
        console.error("FABRIC_QR_SESSION_LOAD_FAILED", {
          sessionId,
          shortCode,
          hasToken: Boolean(token),
          errorCode: "invalid-link",
          errorMessage: "Missing shortCode or legacy sessionId/token.",
          currentUrl: redactFabricUploadTokenFromUrl(window.location.href),
        });
        setSessionState({
          loading: false,
          error: "This upload link is invalid.",
          session: null,
        });
        setMobileSteps((prev) => ({ ...prev, session: "failed" }));
        return;
      }

      try {
        const result = await getFabricUploadJson("getFabricPhotoUploadSessionPublicHttp", {
          sessionId,
          shortCode,
          token,
        });
        if (!active) return;
        const loadedSession = result?.session || result || null;
        const status = String(loadedSession?.status || "").toLowerCase();
        if (status === "uploaded") {
          setUploaded(true);
        }
        setMobileSteps((prev) => ({
          ...prev,
          session: "loaded",
          complete: status === "uploaded" ? "uploaded" : prev.complete,
        }));
        setSessionState({
          loading: false,
          error:
            status && !["pending", "uploaded"].includes(status)
              ? "This upload link is no longer active. Please generate a new QR code."
              : "",
          session: loadedSession,
        });
      } catch (error) {
        if (!active) return;
        console.error("FABRIC_QR_SESSION_LOAD_FAILED", {
          code: error?.code,
          message: error?.message,
          errorCode: error?.code,
          errorMessage: error?.message,
          stack: error?.stack,
          firestorePath: sessionId ? `public_fabric_upload_sessions/${sessionId}` : "",
          sessionId,
          shortCode,
          hasToken: Boolean(token),
          sessionEndpoint: getFabricUploadEndpoint("getFabricPhotoUploadSessionPublicHttp"),
        });
        setMobileSteps((prev) => ({ ...prev, session: "failed" }));
        setSessionState({
          loading: false,
          error: getFriendlyFabricUploadError(error, "This upload link is invalid."),
          session: null,
        });
      }
    };

    loadSession();
    return () => {
      active = false;
    };
  }, [sessionId, shortCode, token]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setUploadError("");
    setUploaded(false);
    if (!file) {
      setSelectedFile(null);
      setMobileSteps((prev) => ({ ...prev, file: "idle" }));
      return;
    }
    const validation = validateManagedImageFile(file, { maxBytes: MAX_FABRIC_QR_UPLOAD_BYTES });
    console.log("FABRIC_QR_FILE_SELECTED", {
      sessionId,
      shortCode,
      fileName: file.name || "",
      fileType: file.type || "",
      fileSize: file.size || 0,
      validationStatus: validation.valid ? "accepted" : "rejected",
      validationMessage: validation.valid ? "" : validation.message,
    });
    if (!validation.valid) {
      setSelectedFile(null);
      setUploadError(validation.message);
      setMobileSteps((prev) => ({ ...prev, file: "failed" }));
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    setMobileSteps((prev) => ({ ...prev, file: "selected", upload: "idle", complete: "idle" }));
  };

  const uploadSelectedPhoto = async () => {
    if (!selectedFile || !sessionState.session || uploading) return;

    setUploading(true);
    setUploadError("");
    setMobileSteps((prev) => ({ ...prev, upload: "starting", complete: "idle" }));

    try {
      console.log("FABRIC_QR_UPLOAD_START", {
        sessionId,
        shortCode,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        storagePath: sessionState.session?.storagePathPrefix
          ? `${sessionState.session.storagePathPrefix}<generated-file>`
          : "",
        uploadEndpoint: getFabricUploadEndpoint("uploadFabricPhotoFromSessionPublicHttp"),
      });

      const optimized = await optimizeImageFile({
        file: selectedFile,
        maxLongEdge: 1600,
        quality: 0.85,
        preserveTransparency: false,
        targetBytes: DEFAULT_TARGET_IMAGE_BYTES,
        hardMaxBytes: DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
      });
      const uploadFile = optimized.optimizedFile || selectedFile;
      const thumbnail = await createThumbnailFile({
        file: uploadFile,
        maxLongEdge: 300,
        quality: 0.78,
        preserveTransparency: false,
      });
      const imageData = dataUrlToPayload(
        await readFileAsDataUrl(uploadFile),
        uploadFile.type || selectedFile.type || "image/jpeg"
      );
      const thumbnailData = thumbnail.thumbnailFile
        ? dataUrlToPayload(
            await readFileAsDataUrl(thumbnail.thumbnailFile),
            thumbnail.thumbnailFile.type || "image/webp"
          )
        : null;

      setMobileSteps((prev) => ({ ...prev, upload: "uploading" }));
      const result = await postFabricUploadJson("uploadFabricPhotoFromSessionPublicHttp", {
        sessionId,
        shortCode,
        token,
        fileName: uploadFile.name || selectedFile.name || "fabric-photo",
        contentType: imageData.contentType,
        imageBase64: imageData.base64,
        thumbnailBase64: thumbnailData?.base64 || "",
        thumbnailContentType: thumbnailData?.contentType || "",
        originalFilename: selectedFile.name || "",
        originalSizeBytes: selectedFile.size || 0,
        optimizedSizeBytes: uploadFile.size || 0,
        compressionTargetBytes: DEFAULT_TARGET_IMAGE_BYTES,
        compressionHardMaxBytes: DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
        width: optimized.width || 0,
        height: optimized.height || 0,
        originalWidth: optimized.originalWidth || 0,
        originalHeight: optimized.originalHeight || 0,
        compressionRatio: optimized.compressionRatio || 1,
        compressionStatus: optimized.compressionStatus || "unknown",
        compressionQuality: optimized.compressionQuality || 0,
        compressionLongEdge: optimized.compressionLongEdge || 0,
        deviceInfo: {
          userAgent: navigator.userAgent || "",
          platform: navigator.platform || "",
          language: navigator.language || "",
        },
      });

      console.log("FABRIC_QR_STORAGE_UPLOAD_SUCCESS", {
        sessionId,
        shortCode,
        storagePath: result?.uploadedPhoto?.storagePath || "",
        thumbStoragePath: result?.uploadedPhoto?.thumbStoragePath || "",
        downloadUrl: result?.uploadedPhoto?.downloadUrl || "",
      });
      console.log("FABRIC_QR_UPLOAD_SUCCESS", { sessionId });
      setMobileSteps((prev) => ({ ...prev, upload: "uploaded", complete: "complete" }));
      setUploaded(true);
      setSelectedFile(null);
    } catch (error) {
      const storagePath = sessionState.session?.storagePathPrefix
        ? `${sessionState.session.storagePathPrefix}<generated-file>`
        : "";
      console.error("FABRIC_QR_STORAGE_UPLOAD_FAILED", {
        sessionId,
        shortCode,
        storagePath,
        code: error?.code,
        message: error?.message,
        errorCode: error?.code,
        errorMessage: error?.message,
        stack: error?.stack,
        uploadEndpoint: getFabricUploadEndpoint("uploadFabricPhotoFromSessionPublicHttp"),
      });
      console.error("FABRIC_QR_COMPLETE_FAILED", {
        sessionId,
        shortCode,
        errorCode: error?.code,
        errorMessage: error?.message,
        stack: error?.stack,
        firestorePath: sessionId ? `public_fabric_upload_sessions/${sessionId}` : "",
      });
      setMobileSteps((prev) => ({ ...prev, upload: "failed", complete: "failed" }));
      setUploadError(getFriendlyFabricUploadError(error));
    } finally {
      setUploading(false);
    }
  };

  const session = sessionState.session;
  const cardBg = "#ffffff";
  const pageBg =
    "radial-gradient(circle at top left, rgba(37,99,235,0.16), transparent 34%), linear-gradient(135deg, #eef2ff 0%, #f8fafc 46%, #ecfeff 100%)";

  return (
    <main
      style={{
        minHeight: "100vh",
        background: pageBg,
        display: "grid",
        placeItems: "center",
        padding: 20,
        color: "#0f172a",
      }}
    >
      <section
        style={{
          width: "min(520px, 100%)",
          background: cardBg,
          borderRadius: 28,
          padding: "28px 24px",
          boxShadow: "0 24px 70px rgba(15,23,42,0.14)",
          border: "1px solid rgba(203,213,225,0.8)",
          display: "grid",
          gap: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 16,
              background: "#0f172a",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 950,
              letterSpacing: "-0.05em",
            }}
          >
            FQ
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 950, letterSpacing: "-0.04em" }}>
              FabriQuote
            </div>
            <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>
              Fabric photo upload
            </div>
          </div>
        </div>

        {sessionState.loading ? (
          <div style={{ display: "grid", placeItems: "center", gap: 12, padding: 32 }}>
            <Loader2 size={34} style={{ animation: "spin 1s linear infinite" }} />
            <div style={{ fontSize: 14, fontWeight: 800 }}>Checking upload link...</div>
          </div>
        ) : sessionState.error ? (
          <div
            style={{
              padding: 18,
              borderRadius: 20,
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 14,
              fontWeight: 800,
              lineHeight: 1.6,
            }}
          >
            {sessionState.error}
          </div>
        ) : uploaded ? (
          <div
            style={{
              padding: 22,
              borderRadius: 22,
              background: "#ecfdf5",
              color: "#065f46",
              display: "grid",
              gap: 10,
              justifyItems: "center",
              textAlign: "center",
            }}
          >
            <CheckCircle2 size={42} />
            <div style={{ fontSize: 20, fontWeight: 950 }}>
              Fabric photo uploaded successfully.
            </div>
            <div style={{ fontSize: 13, color: "#047857", lineHeight: 1.55 }}>
              You can close this page. The desktop quote screen will update automatically.
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                padding: 16,
                borderRadius: 22,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                Uploading photo for
              </div>
              <div style={{ fontSize: 21, fontWeight: 950, letterSpacing: "-0.03em" }}>
                {session?.fabricDisplayName || "Selected fabric"}
              </div>
              <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.55 }}>
                {[session?.brandName, session?.catalogueName, session?.srNumber ? `SR-${session.srNumber}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {session?.expiresAt ? (
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
                  Link expires at {formatExpiry(session.expiresAt)}
                </div>
              ) : null}
            </div>

            <div
              style={{
                padding: 14,
                borderRadius: 18,
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                color: "#3730a3",
                fontSize: 13,
                fontWeight: 850,
                lineHeight: 1.65,
              }}
            >
              1. Tap Take Photo or Choose Photo<br />
              2. Select the fabric image<br />
              3. Tap Upload Fabric Photo
            </div>

            {showMobileDebug ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  color: "#475569",
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1.7,
                }}
              >
                <div style={{ color: "#0f172a", fontWeight: 950 }}>Upload diagnostics</div>
                <div>Session loaded: {mobileSteps.session}</div>
                <div>File selected: {mobileSteps.file}</div>
                <div>Upload: {mobileSteps.upload}</div>
                <div>Quote linked: {mobileSteps.complete}</div>
              </div>
            ) : null}

            <div
              style={{
                minHeight: 190,
                borderRadius: 24,
                border: "1.5px dashed #93c5fd",
                background: "#eff6ff",
                display: "grid",
                placeItems: "center",
                padding: 18,
                textAlign: "center",
                cursor: uploading ? "wait" : "pointer",
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Selected fabric preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 260,
                    borderRadius: 18,
                    objectFit: "contain",
                    boxShadow: "0 14px 32px rgba(15,23,42,0.18)",
                  }}
                />
              ) : (
                <div style={{ display: "grid", gap: 10, justifyItems: "center" }}>
                  <div
                    style={{
                      width: 62,
                      height: 62,
                      borderRadius: 20,
                      background: "#2563eb",
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <Camera size={30} />
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 950 }}>
                    Take Photo / Choose Photo
                  </div>
                  <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                    Use your camera or gallery. JPG, PNG, and WebP are supported.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 10,
                      marginTop: 4,
                    }}
                  >
                    <label
                      style={{
                        minHeight: 40,
                        borderRadius: 999,
                        background: uploading ? "#94a3b8" : "#2563eb",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 16px",
                        fontSize: 13,
                        fontWeight: 950,
                        cursor: uploading ? "not-allowed" : "pointer",
                      }}
                    >
                      Take Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        disabled={uploading}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                    </label>
                    <label
                      style={{
                        minHeight: 40,
                        borderRadius: 999,
                        background: "#ffffff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 16px",
                        fontSize: 13,
                        fontWeight: 950,
                        cursor: uploading ? "not-allowed" : "pointer",
                      }}
                    >
                      Choose Photo
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploading}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {uploadError ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  background: "#fef2f2",
                  color: "#991b1b",
                  fontSize: 13,
                  fontWeight: 800,
                  lineHeight: 1.5,
                }}
              >
                {uploadError}
              </div>
            ) : null}

            <button
              type="button"
              disabled={!selectedFile || uploading}
              onClick={uploadSelectedPhoto}
              style={{
                minHeight: 50,
                border: 0,
                borderRadius: 16,
                background: !selectedFile || uploading ? "#94a3b8" : "#2563eb",
                color: "#fff",
                fontSize: 15,
                fontWeight: 950,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                cursor: !selectedFile || uploading ? "not-allowed" : "pointer",
                boxShadow: !selectedFile || uploading ? "none" : "0 12px 28px rgba(37,99,235,0.28)",
              }}
            >
              {uploading ? <Loader2 size={18} /> : selectedFile ? <UploadCloud size={18} /> : <ImagePlus size={18} />}
              {uploading ? "Uploading..." : "Upload Fabric Photo"}
            </button>
          </>
        )}
      </section>
    </main>
  );
}
